<script setup>
import {getCurrentInstance, onMounted, onBeforeUnmount, ref} from "vue";
import StormCard from "./StormCard.vue";
import moment from "moment/moment";
import axios from "axios";
import forestreeHelpers from "@/helpers/forestreeHelpers";

const props = defineProps([
    "works",
    "tasks",
    "totalTrees",
    "totalParkTrees",
    "totalStreetTrees",
    "totalVacantTrees",
    "totalVacantApproved",
    "totalWaterTrees",
    "groups",
    "loading",
    "tour",
    "access",
    "permissions",
    "storm_mode",
    "activeStorms"
]);


const today = ref(moment().format("dddd Do MMM YYYY"));
const onTour = ref(false);
const offset = ref(0); // Offset for paginated days
const limit = ref(2); // Always fetch 2 days at a time
const isLoadingMore = ref(false);
const hasMore = ref(true); // Track if more days are available
const activitiesByDay = ref([]); // Array of { day: string, events: array }
const allActivities = ref([]);
const callbacks = ref({
    onSkip: null,
    onFinish: null,
});
const tourInstance = ref(null);

const steps = ref([
    {
        target: "#v-step-0",
        header: {
            title: "Quick Summary",
        },
        params: {
            placement: "left",
            enableScrolling: true,
        },
        content: `Here you will find a quick summary of your Urban Forest, this
          data will update live as you collect, plant and remove trees.`,
    },
    {
        target: "#v-step-2",
        header: {
            title: "Activity Feed",
        },
        params: {
            placement: "left-start",
            enableScrolling: true,
        },
        content: `The Activity Feed gives you a live feed on what work is being
          undertaken. At the bottom of each day, you will see a total of trees planted, removed, watered and updated.`,
    },
    {
        target: "#v-step-3",
        header: {
            title: "Maps!",
        },
        params: {
            placement: "bottom",
            enableScrolling: true,
        },
        content: `Here is where you do all the work!`,
    },
    {
        target: "#v-step-4",
        header: {
            title: "Inspections",
        },
        params: {
            placement: "bottom",
            enableScrolling: true,
        },
        content: `The <b>Inspections</b> map is your main map, use it to undertake assessments, add trees and allocate works.`,
    },
    {
        target: "#v-step-5",
        header: {
            title: "Works",
        },
        params: {
            placement: "bottom",
            enableScrolling: true,
        },
        content: `The <b>Works</b> map will show you were all the open works are. And you can undertaken proactive work if required. <br><br>A list of these works can
          be found on the left-hand side of this page.`,
    },
    {
        target: "#v-step-6",
        header: {
            title: "Planting",
        },
        params: {
            placement: "bottom",
            enableScrolling: true,
        },
        content: `Use the <b>Planting</b> map to locate planting opportunities, assess them and plan your planting programs for years in advance. <br><br>A planting list
          be found on the left-hand side of this page.`,
    },
    {
        target: "#v-step-7",
        header: {
            title: "Stumps",
        },
        params: {
            placement: "bottom",
            enableScrolling: true,
        },
        content: `The <b>Stumps</b> will display all stumps and their sizes.<br><br>
          Use the Stumps Removal List on the left to allocate removals to contractors.`,
    },
    {
        target: "#v-step-8",
        header: {
            title: "Watering",
        },
        params: {
            placement: "bottom",
            enableScrolling: true,
        },
        content: `The <b>Watering</b> map allows you to water newly planted trees. Trees are automatically added to this map based on their planted date.<br><br>
          You can add/remove trees from the Watering map using the Flag Tree option.`,
    },
]);


const fetchActivities = async () => {
    isLoadingMore.value = true;
    try {
        const response = await axios.get("/api/dashboard/activity-only", {
            params: {
                offset: offset.value,
                limit: limit.value
            }
        });
        // The backend should return an array of activities for the requested days
        // Group by day (format: 'dddd Do MMM YYYY')
        const grouped = {};
        response.data.forEach(event => {
            const day = moment(event.created_at).format("YYYY-MM-DD");
            if (!grouped[day]) grouped[day] = [];
            grouped[day].push(event);
        });
        const newDays = Object.entries(grouped).map(([day, events]) => ({ day, events }));
        // Append new days
        activitiesByDay.value = [...activitiesByDay.value, ...newDays];
        // If fewer than limit days returned, no more data
        if (newDays.length < limit.value) {
            hasMore.value = false;
        }
    } catch (e) {
        // Optionally handle error
        hasMore.value = false;
    }
    isLoadingMore.value = false;
};

onMounted(() => {
    localStorage.removeItem("coords");
    localStorage.removeItem("zoom");
    const app = getCurrentInstance();
    tourInstance.value = app?.appContext?.config?.globalProperties?.$tours;
    callbacks.value.onSkip = disableTour;
    callbacks.value.onFinish = disableTour;
    if (props.tour) {
        setTimeout(() => {
            startTour();
        }, 2000);
    }
    // Initial fetch
    fetchActivities();
    window.addEventListener('scroll', handleScroll);
});

const disableTour = () => {
    axios.get("/api/settings/users/tour");
    onTour.value = false;
};

const startTour = () => {
    onTour.value = true;
    if (tourInstance.value && tourInstance.value["myTour"]) {
        tourInstance.value["myTour"].start();
    }
};
const getWorkTask = (taskId) => {
    if (parseInt(taskId)) {
        var length = props.tasks.length;
        for (var i = 0; i < length; i++) {
            if (props.tasks[i].id === parseInt(taskId)) {
                return props.tasks[i].task;
            }
        }
    }
};



const loadMore = async () => {
    if (isLoadingMore.value || !hasMore.value) return;
    offset.value += limit.value;
    await fetchActivities();
};


// Infinite scroll: fetch more when reaching bottom of #v-step-2 (div containing activity feed)
const handleScroll = () => {
    const feed = document.getElementById('v-step-2');
    if (!feed) return;
    const rect = feed.getBoundingClientRect();
    // If the bottom of the feed is within 100px of the viewport bottom
    if (
        rect.bottom - 100 <= window.innerHeight &&
        !isLoadingMore.value && hasMore.value && !props.loading
    ) {
        loadMore();
    }
};

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
});



</script>

<template>
    <div class="flex flex-wrap w-full xl:flex-no-wrap lg:w-3/4">
        <div class="w-full px-4 text-center xl:w-3/4 md:p-4">
            <div
                v-if="
          permissions.map_insp ||
          permissions.map_work ||
          permissions.map_plant ||
          permissions.map_stump ||
          permissions.map_water
        "
                id="v-step-3"
                class="w-full h-auto pb-4 bg-white xl:pb-0 xl:h-56 forestree-box all-rounded"
            >
                <p class="px-6 pt-4 text-2xl text-left md:px-12 text-slate-400">Maps</p>
                <div
                    class="flex flex-wrap items-center justify-center w-full px-4 uppercase sm:flex-no-wrap text-slate-400 sm:px-8"
                >
                    <div
                        v-if="permissions.map_insp"
                        id="v-step-4"
                        class="w-1/2 px-2 py-6 border-4 border-white rounded-lg sm:w-1/5 bg-slate-100 hover:bg-slate-200"
                    >
                        <a href="/map">
                            <img
                                src="/images/icons/Inspection.png"
                                class="mx-auto"
                                width="48px"
                            />
                            <p class="pt-1">Inspections</p>
                        </a>
                    </div>
                    <div
                        v-if="permissions.map_work"
                        id="v-step-5"
                        class="w-1/2 px-2 py-6 border-4 border-white rounded-lg sm:w-1/5 bg-slate-100 hover:bg-slate-200"
                    >
                        <a href="/maintenance-map">
                            <img src="/images/icons/Works.png" class="mx-auto" width="48px"/>
                            <p class="pt-1">Works</p>
                        </a>
                    </div>
                    <div
                        v-if="permissions.map_plant"
                        id="v-step-6"
                        class="w-1/2 px-2 py-6 border-4 border-white rounded-lg sm:w-1/5 bg-slate-100 hover:bg-slate-200"
                    >
                        <a href="/vacant-map">
                            <img
                                src="/images/icons/Planting.png"
                                class="mx-auto"
                                width="48px"
                            />
                            <p class="pt-1">Planting</p>
                        </a>
                    </div>
                    <div
                        v-if="permissions.map_stump"
                        id="v-step-7"
                        class="w-1/2 px-2 py-6 border-4 border-white rounded-lg sm:w-1/5 bg-slate-100 hover:bg-slate-200"
                    >
                        <a href="/stumps">
                            <img src="/images/icons/Stump.png" class="mx-auto" width="48px"/>
                            <p class="pt-1">Stumps</p>
                        </a>
                    </div>
                    <div
                        v-if="permissions.map_water"
                        id="v-step-8"
                        class="w-1/2 px-2 py-6 border-4 border-white rounded-lg sm:w-1/5 bg-slate-100 hover:bg-slate-200"
                    >
                        <a href="/watering">
                            <img
                                src="/images/icons/Watering.png"
                                class="mx-auto"
                                width="48px"
                            />
                            <p class="pt-1">Watering</p>
                        </a>
                    </div>
                </div>
            </div>

            <div class="block w-full py-4 xl:hidden">

                <storm-card
                    class="mb-4"
                    :permissions="permissions"
                    :storm_mode="storm_mode"
                    :activeStorms="activeStorms"
                />

                <div
                    class="justify-center w-full h-auto p-8 text-sm bg-white sm:flex sm:flex-wrap md:flex-no-wrap text-slate-600 forestree-box all-rounded"
                >
                    <div class="justify-center sm:flex-1">
                        <p class="uppercase text-slate-400">Total Trees</p>
                        <p class="py-2 text-3xl leading-none text-green-500 xl:text-6xl">
                            {{ totalTrees }}
                        </p>
                    </div>
                    <div class="justify-center sm:flex-1">
                        <p class="uppercase text-slate-400">Street Trees</p>
                        <p class="py-2 text-3xl leading-none text-blue-500">
                            {{ totalStreetTrees }}
                        </p>
                    </div>
                    <div class="justify-center sm:flex-1">
                        <p class="uppercase text-slate-400">Park/Reserve Trees</p>
                        <p class="py-2 text-3xl leading-none text-blue-500">
                            {{ totalParkTrees }}
                        </p>
                    </div>
                    <div class="justify-center sm:flex-1">
                        <p class="uppercase text-slate-400">Planting Spaces</p>
                        <p class="py-2 text-3xl leading-none text-blue-500">
                            {{ totalVacantTrees }}
                        </p>
                    </div>
                    <div class="justify-center sm:flex-1">
                        <p class="uppercase text-slate-400">Watering List</p>
                        <p class="py-2 text-3xl leading-none text-blue-500">
                            {{ totalWaterTrees }}
                        </p>
                    </div>
                </div>

            </div>

            <div class="w-full h-auto mt-4 text-left text-slate-400">
                <p
                    :class="
            permissions.map_insp ||
            permissions.map_work ||
            permissions.map_plant ||
            permissions.map_stump ||
            permissions.map_water
              ? ''
              : 'text-slate-400 lg:text-white mt-8'
          "
                    class="my-2 text-2xl"
                >
                    Activity Feed
                </p>

                <div v-if="loading || (isLoadingMore && offset === 0 )" class="w-full h-56 text-center">
                    <img src="/loading.gif" width="60" class="mx-auto"/>
                    <h3 class="mb-3 text-xl leading-none text-slate-400">
                        Growing your stats...
                    </h3>
                    <h3 class="mb-3 text-lg leading-none text-slate-400">Please wait</h3>
                </div>

                <div v-if="activitiesByDay.length && !loading" id="v-step-2">
                    <div v-for="({ day, events }) in activitiesByDay" :key="day">
                        <p class="mt-4 mb-2 text-slate-400">
                            {{ day == today ? "Today" : moment(day).format("dddd Do MMM YYYY") }}
                        </p>
                        <div class="p-6 pt-3 mb-2 bg-white forestree-box all-rounded">
                            <!-- Daily summary counts -->
                            <div class="flex flex-wrap items-center gap-2 pt-2 pb-4 mb-4 border-b">
                                <a v-if="events.filter(e => e.type === 'p').length" 
                                    :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=planted&date=' + day
                                          : ''
                                      "
                                    class="flex items-center pl-2 pr-4 py-2 text-sm font-semibold text-green-700 bg-green-100 hover:bg-green-50 rounded-full">
                                    <img
                                        class="w-5 h-5 mt-0 mr-2 rounded-full md:w-8 md:h-8 md:mt-0"
                                        :src="'images/icons/' + forestreeHelpers.getIcon('p') + '.png'"
                                        alt="Planted"
                                    />
                                    Planted: {{ events.filter(e => e.type === 'p').reduce((sum, e) => sum + (e.total || 1), 0) }}
                                </a>
                                <a v-if="events.filter(e => e.type === 'i').length" 
                                    :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=inspected&date=' + day
                                          : ''
                                      "
                                    class="flex items-center pl-2 pr-4 py-2 text-sm font-semibold text-blue-700 bg-blue-100 hover:bg-blue-50 rounded-full">
                                    <img
                                        class="w-5 h-5 mt-0 mr-2 rounded-full md:w-8 md:h-8 md:mt-0"
                                        :src="'images/icons/' + forestreeHelpers.getIcon('i') + '.png'"
                                        alt="Inspected"
                                    />
                                    Inspected: {{ events.filter(e => e.type === 'i').length }}
                                </a>
                                <span v-if="events.filter(e => e.type === 'c').length" class="flex items-center pl-2 pr-4 py-2 text-sm font-semibold text-orange-700 bg-orange-100 rounded-full">
                                    <img
                                        class="w-5 h-5 mt-0 mr-2 rounded-full md:w-8 md:h-8 md:mt-0"
                                        :src="'images/icons/' + forestreeHelpers.getIcon('m') + '.png'"
                                        alt="Works"
                                    />
                                    Works: {{ events.filter(e => e.type === 'm').reduce((sum, e) => sum + (e.total || 1), 0) }}
                                </span>
                                <a v-if="events.filter(e => e.type === 'w').length" 
                                    :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=watered&date=' + day
                                          : ''
                                      "
                                    class="flex items-center pl-2 pr-4 py-2 text-sm font-semibold text-teal-700 bg-teal-100 hover:bg-teal-50 rounded-full">
                                    <img
                                        class="w-5 h-5 mt-0 mr-2 rounded-full md:w-8 md:h-8 md:mt-0"
                                        :src="'images/icons/' + forestreeHelpers.getIcon('w') + '.png'"
                                        alt="Watered"
                                    />
                                    Watered: {{ events.filter(e => e.type === 'w').reduce((sum, e) => sum + (e.total || 1), 0) }}
                                </a>
                                <span v-if="events.filter(e => e.type === 's').length" class="flex items-center pl-2 pr-4 py-2 text-sm font-semibold text-slate-700 bg-slate-100 rounded-full">
                                    <img
                                        class="w-5 h-5 mt-0 mr-2 rounded-full md:w-8 md:h-8 md:mt-0"
                                        :src="'images/icons/' + forestreeHelpers.getIcon('s') + '.png'"
                                        alt="Stumps Removed"
                                    />
                                    Stumps Removed: {{ events.filter(e => e.type === 's').reduce((sum, e) => sum + (e.total || 1), 0) }}
                                </span>
                                <a v-if="events.filter(e => e.type === 'c').length"
                                    :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=collected&date=' + day
                                          : ''
                                      "
                                    class="flex items-center pl-2 pr-4 py-2 text-sm font-semibold text-yellow-700 bg-yellow-100 hover:bg-yellow-50 rounded-full">
                                    <img
                                        class="w-5 h-5 mt-0 mr-2 rounded-full md:w-8 md:h-8 md:mt-0"
                                        :src="'images/icons/' + forestreeHelpers.getIcon('c') + '.png'"
                                        alt="Data Collected"
                                    />
                                    Data Collected: {{ events.filter(e => e.type === 'c').reduce((sum, e) => sum + (e.total || 1), 0) }}
                                </a>
                            </div>
                            <div
                                class="py-3"
                                v-for="(work, key, index) in events.filter(e => e.work_tasks !== '[]')"
                                :key="index"
                                :class="[
                                    key + 1 === Object.keys(events).length
                                        ? 'border-0'
                                        : ' border-b border-1',
                                ]"
                            >
                                <div class="flex">
                                    <img
                                        class="w-5 h-5 mt-0 mr-2 rounded-full md:w-10 md:h-10 md:mt-0 md:mr-4"
                                        :src="
                              'images/icons/' +
                              forestreeHelpers.getIcon(work.type) +
                              '.png'
                            "
                                    />
                                    <div class="w-full text-sm">
                                        <p class="text-lg leading-none text-slate-900">
                                            {{ forestreeHelpers.activityFeedType(work.type) }}
                                            <span
                                                v-if="work.type === 'm' || work.type === 'i'"
                                                class="text-sm text-blue-500 text-slate-600"
                                            >
                                                <a
                                                    v-if="work.tree_id > 0"
                                                    :href="'/map?treeId=' + work.tree_id"
                                                >{{ work.id }}</a
                                                >
                                            </span>
                                            </p>
                                            
                                            <div
                                                v-if="work.storm_event"
                                                class="inline-block px-1 py-0 mt-2 text-sm rounded-full bg-slate-800 text-slate-200">
                                                <p class="flex py-1 pr-2 text-sm font-semibold text-slate-100">
                                                    <img src="/images/icons/storm.png" class="w-6 h-6 mr-2"/>
                                                    Storm Event
                                                </p>
                                            </div>
                                            <div v-if="forestreeHelpers.formatWorkTask(work.work_tasks).length" class="py-2">
                                            <span
                                                v-if="work.crm_id"
                                                class="inline-block px-2 mb-1 mr-1 text-sm bg-red-500 rounded-full py-05 text-slate-100"
                                            >
                                                CR# {{ work.crm_id }}
                                            </span>
                                            <template
                                                v-for="(task, index) in forestreeHelpers.formatWorkTask(
                                                    work.work_tasks
                                                    )"
                                                :key="index"
                                            >
                                            <span
                                                v-if="getWorkTask(task)"
                                                class="inline-block px-2 mb-1 mr-1 text-sm rounded-full bg-slate-500 py-05 text-slate-200"
                                            >
                                              {{ getWorkTask(task) }}
                                            </span>
                                            </template>
                                        </div>
                                        <div
                                            v-if="work.notes && work.notes !== ' - '"
                                            class="items-center p-2 mt-1 mb-2 text-sm rounded-lg bg-slate-100 text-slate-700"
                                            role="alert"
                                        >
                                            {{ work.notes }}
                                        </div>
                                        <!-- <div
                                            v-if="work.type === 'i' && work.name === 'Data Updates'"
                                        >
                                            <a
                                                class="hover:text-blue-600"
                                                :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=inspected&date=' +
                                            forestreeHelpers.justDateWithDash(work.created_at)
                                          : ''
                                      "
                                            ><b>{{ work.total }}</b> tree inspection(s) have been
                                                undertaken.</a
                                            >
                                        </div> -->
                                        <div v-if="work.type === 'p'">
                                            <a
                                                class="hover:text-blue-600"
                                                :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=planted&date=' +
                                            forestreeHelpers.justDateWithDash(work.created_at)
                                          : ''
                                      "
                                            ><b>{{ work.total }}</b> tree(s) have been planted</a
                                            >
                                        </div>
                                        <div v-if="work.type === 'w'">
                                            <a
                                                class="hover:text-blue-600"
                                                :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=watered&date=' +
                                            forestreeHelpers.justDateWithDash(work.created_at)
                                          : ''
                                      "
                                            ><b>{{ work.total }}</b> tree(s) have been watered</a
                                            >
                                        </div>
                                        <div v-if="work.type === 's'">
                                            <a
                                                class="hover:text-blue-600"
                                                :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=removed&date=' +
                                            forestreeHelpers.justDateWithDash(work.created_at)
                                          : ''
                                      "
                                            ><b>{{ work.total }}</b> trees(s)/stump(s) have been
                                                removed</a
                                            >
                                        </div>
                                        <div v-if="work.type === 'c'">
                                            <a
                                                class="hover:text-blue-600"
                                                :href="
                                        permissions && permissions.reporting_access
                                          ? '/reporting?type=collected&date=' +
                                            forestreeHelpers.justDateWithDash(work.created_at)
                                          : ''
                                      "
                                            ><b>{{ work.total }}</b> existing trees(s) and/or
                                                planting location(s) have been collected</a
                                            >
                                        </div>
                                        <p
                                            v-if="
                                        ['w', 'p', 's', 'c'].includes(work.type) ||
                                        work.name === 'Data Updates'
                                      "
                                            class="text-right"
                                        >
                                            {{ forestreeHelpers.justDate(work.created_at) }}
                                        </p>
                                        <p v-else class="text-right">
                                            {{ work.name }},
                                            {{ forestreeHelpers.dateTime(work.created_at) }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Show More Button -->
                    <!-- Infinite scroll: No button, loading indicator only -->
                    <div v-if="isLoadingMore && hasMore" class="py-4 text-center">
                        <div class="w-full h-56 text-center">
                            <img src="/loading.gif" width="60" class="mx-auto"/>
                            <h3 class="mb-3 text-xl leading-none text-slate-400">
                                Growing your stats...
                            </h3>
                            <h3 class="mb-3 text-lg leading-none text-slate-400">Please wait</h3>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="hidden p-4 xl:block md:w-1/4">
            <div
                id="v-step-0"
                class="w-full h-auto px-6 py-8 bg-white text-slate-600 forestree-box all-rounded"
            >
                <p class="uppercase text-slate-400">Total Trees</p>
                <p class="pb-6 text-3xl leading-none text-green-500 lg:text-5xl 2xl:text-6xl">
                    {{ totalTrees }}
                </p>
                <p class="uppercase text-slate-400">Street Trees</p>
                <p class="pb-6 text-3xl leading-none text-blue-500">
                    {{ totalStreetTrees }}
                </p>
                <p class="uppercase text-slate-400">Park/Reserve Trees</p>
                <p class="pb-6 text-3xl leading-none text-blue-500">
                    {{ totalParkTrees }}
                </p>
                <p class="uppercase text-slate-400">Planting Spaces</p>
                <p class="pb-1 text-3xl leading-none text-blue-500">
                    {{ totalVacantTrees }}
                </p>

                <div
                    v-if="totalWaterTrees"
                    class="inline-flex items-center text-sm font-medium text-green-600 rounded-full"
                >
                    <!-- <svg class="-ml-1 mr-0.5 h-5 w-5 flex-shrink-0 self-center text-green-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                      <path fill-rule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clip-rule="evenodd" />
                    </svg> -->
                    <img class="w-4 h-4 mr-1" src="/images/icons/complete.png"/>
                    <span class="sr-only">Approved</span>
                    {{ totalVacantApproved }} Approved
                </div>
                <p class="pt-4 uppercase text-slate-400">Watering List</p>
                <p class="text-3xl leading-none text-blue-500">{{ totalWaterTrees }}</p>
            </div>

            <storm-card
                :permissions="permissions"
                :storm_mode="storm_mode"
                :activeStorms="activeStorms"
            />

            <div
                v-if="groups && groups.length > 0"
                class="w-full h-auto px-6 py-8 mt-3 bg-white text-slate-600 forestree-box all-rounded"
            >
                <div v-for="g in groups" :key="g.name" class="flex justify-between">
                    <p class="uppercase text-slate-400">{{ g.name }}</p>
                    <p class="pl-2 font-semibold text-green-500">{{ g.total }}</p>
                </div>
            </div>

            <div
                class="w-full h-auto px-6 py-6 mt-4 text-center bg-white text-slate-600 forestree-box all-rounded"
            >
                <p class="w-full mb-2">
                    <a
                        v-if="permissions && !permissions.contractor"
                        href="https://support.forestree.app"
                        target="_blank"
                        class="flex justify-center w-full py-1 font-semibold text-center text-white uppercase rounded-lg cursor-pointer bg-slate-400 hover:bg-slate-500"
                    >
                        Knowledge Base
                    </a>
                </p>
                <p
                    @click="startTour()"
                    class="py-1 font-semibold text-center text-white uppercase rounded-lg cursor-pointer bg-slate-400 hover:bg-slate-500"
                >
                    Show Tour
                </p>

            </div>
        </div>

        <v-tour
            name="myTour"
            :steps="steps"
            :callbacks="callbacks"
            :options="{ highlight: true }"
        ></v-tour>
    </div>
</template>
<style scoped>
.py-05 {
    padding-top: 0.125rem;
    padding-bottom: 0.125rem;
}
</style>
